/**
 * 入住时间模块
 */
import React, { memo } from 'react';
import { connect } from 'react-redux';
import { Radio, Select, RadioChangeEvent } from 'antd';
import intl from 'react-intl-universal';

import DateJS, { format2DateStr, format2FullTimeStr, format2TimeStr } from '@/utils/date';
import ContentBlock, { FormItem } from '@/components/Reservation/ContentBlock';
import NumberWithHelper from '@/components/Reservation/NumberInput/InputWithHelper';
import { OccType, DayDepartureOptions, OCC_TYPE_MAP, OCC_VALUE_MAP } from '@/config/constant';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import {
  handleOccTypeChange,
  handleDayTimeChange,
  handleHourTimeChange,
} from '@/redux/actions/reservation/renewal';
import { ReducerState, storeName } from '@/redux/reducers/reservation/renewal';
import { formatDayOutTime, formatHourOutTime } from '@/pages/reservation/utils/formatter';

import { getPopupContainer } from '../../helper';
import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { dayTypeTimeInfo, hourTypeTimeInfo, commonInfo } = state;
  return { dayTypeTimeInfo, hourTypeTimeInfo, occType: commonInfo.occType };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callOccTypeChange: handleOccTypeChange,
  callDayTimeChange: handleDayTimeChange,
  callHourTimeChange: handleHourTimeChange,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const {
    occType,
    dayTypeTimeInfo,
    hourTypeTimeInfo,
    callOccTypeChange,
    callDayTimeChange,
    callHourTimeChange,
  } = props;
  const isDayType = occType === OccType.DayUse;

  const handleOCCTypeChange: (event: RadioChangeEvent) => void = (event) => {
    callOccTypeChange(event.target.value === OccType.DayUse ? OccType.DayUse : OccType.TimeRENT);
  };

  const handleDayDurationChange: (value: string) => void = (value) => {
    let rentTime;
    if (value === null) {
      rentTime = 1;
    } else {
      if (!/^\d*$/.test(value)) return;
      if (value) {
        rentTime = Number(value);
        // 日租房 1-180天
        if (rentTime < 1 || rentTime > 180) return;
      }
    }
    callDayTimeChange({ rentTime });
  };

  const handleDayDepartureChange: (departureTime: string) => void = (departureTime) => {
    callDayTimeChange({
      expectedDepartureTime: DateJS(
        `${format2DateStr(dayTypeTimeInfo.expectedDepartureTime)} ${departureTime}`,
      ),
    });
  };

  const handleHourDurationChange: (value: string) => void = (value) => {
    let rentTime;
    if (value === null) {
      rentTime = 2;
    } else {
      if (!/^\d*$/.test(value)) return;
      if (value) {
        rentTime = Number(value);
        // 日租房 1-180天
        if (rentTime < 2 || rentTime > 6) return;
      }
    }
    callHourTimeChange({ rentTime });
  };

  return (
    <ContentBlock
      title={intl.get('drawer_extension_time_block_title')}
      className={styles.timeInfoContainer}
    >
      <FormItem label={intl.get('drawer_extension_time_old_etd_label')}>
        {format2FullTimeStr(dayTypeTimeInfo.expectedArrivalTime)}
      </FormItem>
      <br />
      <FormItem
        label={intl.get('drawer_extension_time_occ_type_label')}
        className={styles.occTypeItem}
      >
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={occType}
          onChange={handleOCCTypeChange}
        >
          <Radio.Button value={OccType.DayUse}>{OCC_TYPE_MAP[OccType.DayUse]}</Radio.Button>
          <Radio.Button value={OccType.TimeRENT}>{OCC_TYPE_MAP[OccType.TimeRENT]}</Radio.Button>
        </Radio.Group>
      </FormItem>
      <br />
      {isDayType ? (
        <>
          <FormItem
            label={intl.get('app_reservation_occ_duration')}
            className={styles.rentTimeItem}
          >
            <NumberWithHelper value={dayTypeTimeInfo.rentTime} onChange={handleDayDurationChange} />
            <span className={styles.rentTimeUnit}>{OCC_VALUE_MAP[OccType.DayUse]}</span>
          </FormItem>
          <FormItem label={intl.get('app_reservation_etd_time')} className={styles.dayPreOutItem}>
            <span className={styles.outDateWrapper}>
              {formatDayOutTime(
                dayTypeTimeInfo.expectedArrivalTime && dayTypeTimeInfo.rentTime
                  ? dayTypeTimeInfo.expectedDepartureTime
                  : undefined,
              )}
            </span>
            <Select
              placeholder={intl.get('reservation_field_select_default_placeholder')}
              options={DayDepartureOptions}
              value={
                dayTypeTimeInfo.expectedDepartureTime
                  ? format2TimeStr(dayTypeTimeInfo.expectedDepartureTime)
                  : undefined
              }
              onChange={handleDayDepartureChange}
              getPopupContainer={getPopupContainer}
            />
          </FormItem>
        </>
      ) : (
        <>
          <FormItem
            label={intl.get('app_reservation_occ_duration')}
            className={styles.rentTimeItem}
          >
            <NumberWithHelper
              value={hourTypeTimeInfo.rentTime}
              onChange={handleHourDurationChange}
            />
            <span className={styles.rentTimeUnit}>{OCC_VALUE_MAP[OccType.TimeRENT]}</span>
          </FormItem>
          <FormItem label={intl.get('app_reservation_etd_time')} className={styles.hourPreOutItem}>
            <span className={styles.outDateWrapper}>
              {formatHourOutTime(
                hourTypeTimeInfo.expectedArrivalTime && hourTypeTimeInfo.rentTime
                  ? hourTypeTimeInfo.expectedDepartureTime
                  : undefined,
              )}
            </span>
          </FormItem>
        </>
      )}
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
